import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

function isTokenExpired(token) {
  try {
    const decoded = jwtDecode(token);
    if (!decoded.exp) return false;
    // Margem de 60s para evitar requests com token prestes a expirar
    return decoded.exp * 1000 < Date.now() + 60000;
  } catch {
    return true;
  }
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    try {
      const savedToken = localStorage.getItem('authToken');
      if (!savedToken) return;

      if (isTokenExpired(savedToken)) {
        logout();
        return;
      }

      const decodedUser = jwtDecode(savedToken);
      setToken(savedToken);
      setUser(decodedUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Erro ao decodificar o token do localStorage:', error);
      logout();
    } finally {
      setIsInitializing(false);
    }
  }, [logout]);

  // Verifica expiração do token periodicamente (a cada 30s)
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      if (isTokenExpired(token)) {
        logout();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [token, logout]);

  const login = useCallback((nextToken) => {
    if (!nextToken || typeof nextToken !== 'string' || isTokenExpired(nextToken)) {
      logout();
      return false;
    }

    try {
      const decodedUser = jwtDecode(nextToken);
      localStorage.setItem('authToken', nextToken);
      setToken(nextToken);
      setUser(decodedUser);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      logout();
      return false;
    }
  }, [logout]);

  const value = useMemo(
    () => ({ isAuthenticated, isInitializing, user, token, login, logout }),
    [isAuthenticated, isInitializing, user, token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
